<template>
  <section class="mega-menu">
    <nav>
      <section class="section categories">
        <translate tag="h2" class="heading"> Accommodation types </translate>
        <accommodation-types
          :categories-data="accomodationCategoriesData"
          class="accommodation-types"
        />
      </section>

      <popular-destinations />

      <section class="section searches">
        <translate tag="h2" class="heading"> Popular searches </translate>
        <popular-searches :with-image="false" />
      </section>

      <section class="section best">
        <translate tag="h2" class="heading"> Best camping near me </translate>
        <ul>
          <li>
            <pu-search-link
              hierarchy-path="near-me/"
              category="campsites"
              :modify-filters="{ facets: undefined, categoryIds: undefined }"
              exclude-type="newLocation"
            >
              <translate>Camping near me</translate>
            </pu-search-link>
            <pu-search-link
              hierarchy-path="near-me/"
              category="glamping"
              :modify-filters="{ facets: undefined, categoryIds: undefined }"
              exclude-type="newLocation"
            >
              <translate>Glamping near me</translate>
            </pu-search-link>

            <pu-search-link
              hierarchy-path="near-me/"
              category="caravans"
              :modify-filters="{ facets: undefined, categoryIds: undefined }"
              exclude-type="newLocation"
            >
              <translate>Caravan parks near me</translate>
            </pu-search-link>

            <pu-search-link
              hierarchy-path="near-me/"
              category="tourers"
              :modify-filters="{ facets: undefined, categoryIds: undefined }"
              exclude-type="newLocation"
            >
              <translate>Touring caravan sites near me</translate>
            </pu-search-link>

            <pu-search-link
              hierarchy-path="near-me/"
              category="lodges"
              :modify-filters="{ facets: undefined, categoryIds: undefined }"
              exclude-type="newLocation"
            >
              <translate>Lodges near me</translate>
            </pu-search-link>
          </li>
        </ul>
      </section>

      <section v-if="shouldShowArticles" class="section guides">
        <translate tag="h2" class="heading"> How to's & guides </translate>
        <article-list :titles-only="true" />
      </section>

      <section
        v-if="['en-gb', 'fr', 'de', 'nl', 'it', 'es'].includes($i18n.locale)"
        class="section parks"
      >
        <translate tag="h2" class="heading"> National parks </translate>
        <national-parks :with-image="false" />
      </section>
    </nav>
  </section>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */

/* TODO: Add proper typing to resolve type safety warnings */

import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import PopularSearches from '~/components/homepage/PopularSearches.vue'
import { Component, Vue, toNative } from '~/utility/pu-class-decorator'
import { CampsiteCategory } from '../../../../utility/types/CampsiteCategory'

interface CategoryInfo {
  name: string
  to: { facet?: string; category: string }
  iconUrl?: string
}

@Component({
  components: {
    PopularSearches,
  },
})
class MegaMenu extends Vue {
  private getCategorySliderData(category: CampsiteCategory) {
    return {
      name: category.pretty_name,
      to: { category: category.rootSlug },
      iconUrl: category.getImageDataUrl(),
    }
  }

  private getGlampingCategory(iconUrl: string): CategoryInfo {
    return {
      name: this.$t('Glamping'),
      to: { category: 'glamping' },
      iconUrl,
    }
  }

  get accomodationCategoriesData(): CategoryInfo[] {
    const categoriesPresenter = getCampsiteCategoriesPresenter(
      this.$route.params.lang || 'en-gb',
    )
    const categories = categoriesPresenter.bySlugMap
    const categoryData: CategoryInfo[] = []
    for (const [index, [_, category]] of Object.entries(categories).entries()) {
      // glamping is not a proper category so need to push it based on the required index
      if (index === 4) {
        categoryData.push(
          this.getGlampingCategory(
            categoriesPresenter.categoryImagesDataUrls.glamping,
          ),
        )
      }
      if (category.id.toString() !== '13') {
        categoryData.push(this.getCategorySliderData(category))
      }
    }
    return categoryData
  }

  get shouldShowArticles() {
    return this.$i18n.locale.startsWith('en-')
  }
}

export default toNative(MegaMenu)
</script>

<style scoped>
/* desktop only */
.mega-menu {
  left: 1%;
  width: 98%;
  margin: auto;
}

nav {
  background: white;
  min-height: 12em;
  display: grid;
  padding: 2em;
  --grid-layout-gap: 3em;
  --grid-column-count: 3;
  --grid-item-min-width: 16em;
  --gap-count: calc(var(--grid-column-count) - 1);
  --total-gap-width: calc(var(--gap-count) * var(--grid-layout-gap));
  --grid-item-max-width: calc(
    (100% - var(--total-gap-width)) / var(--grid-column-count)
  );
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(max(var(--grid-item-min-width), var(--grid-item-max-width)), 1fr)
  );
  grid-gap: var(--grid-layout-gap);
  row-gap: 1em;
}

.mega-menu :deep(.heading) {
  font-size: 1.25em;
  font-weight: bold;
  color: var(--colour-dark-blue);
  border-bottom: 3px solid var(--colour-price);
  padding: 0 0 0.4em 0;
}

.best .heading,
.mega-menu :deep(.destinations .heading) {
  border-color: var(--colour-orange);
}

.guides .heading,
.searches .heading {
  border-color: var(--colour-light-green);
}

.heading,
legend {
  color: var(--colour-dark-blue);
}

.mega-menu :deep(ul) {
  display: block;
}

.mega-menu :deep(ul.articles.desktop.scrollable) {
  padding: 0;
}

.mega-menu :deep(a .name),
.mega-menu :deep(a .title),
.mega-menu :deep(.articles.desktop a .title),
.mega-menu :deep(.national-parks.desktop a .name) {
  font-size: inherit;
  font-weight: normal;
}

.mega-menu :deep(a) {
  color: var(--colour-dark-blue);
  text-decoration: none;
  display: block;
  font-size: 1em;
  line-height: 1.2;
  margin: 0.5em 0;
}

.mega-menu :deep(a:hover),
.mega-menu :deep(a:focus-visible),
.mega-menu :deep(.national-parks.desktop a:hover),
.mega-menu :deep(.national-parks.desktop a:focus-visible),
a:focus-visible,
a:hover {
  text-decoration: underline;
  box-shadow: none;
  transform: none;
  outline: reset;
  border-radius: 0;
}

.mega-menu :deep(.popular-searches.desktop),
.mega-menu :deep(.national-parks.desktop) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10em, max-content)) !important;
  grid-auto-rows: unset;
  row-gap: unset;
  column-gap: 2em;
  justify-content: space-between;
  align-items: center;
}

.mega-menu :deep(.popular-searches.desktop a),
.mega-menu :deep(.national-parks.desktop a) {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 0.25em 0;
  overflow: visible;
  order: unset !important;
}

.mega-menu :deep(.popular-searches.desktop a span) {
  color: inherit;
  position: relative;
  bottom: auto;
  left: auto;
  padding: 0;
  text-shadow: none;
  font-weight: normal;
  font-size: inherit;
}

.accommodation-types.desktop,
.accommodation-types.desktop :deep(div),
.accommodation-types.desktop :deep(div a) {
  display: block;
  font-size: inherit;
  text-align: left;
}

.accommodation-types.desktop :deep(a img) {
  width: auto;
  height: 1.6em;
  margin: 0.15em 0;
  max-width: 3em;
  justify-self: center;
  align-self: center;
}

.accommodation-types.desktop :deep(a.card) {
  display: grid;
  grid-template-columns: 3em auto;
  grid-template-rows: auto;
  column-gap: 0.5em;
  margin: 0;
  box-shadow: none;
  background: inherit;
}

.accommodation-types.desktop :deep(fieldset) {
  border-radius: 0;
  border: none;
  padding: 0;
}

.accommodation-types.desktop :deep(legend) {
  font-size: inherit;
  padding: 0 0 0.5em 0;
  margin-left: 0;
}

.accommodation-types.desktop :deep(a) {
  transition: none;
  border-radius: none;
  padding: 0;
}

.accommodation-types.desktop :deep(a:hover),
.accommodation-types.desktop :deep(a:focus-visible) {
  box-shadow: none;
  transform: none;
  outline: reset;
}
</style>
